import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppRouteNamesList, RoleTypes } from 'models';
import { moduleRoutesList } from 'modules/admin-panel/router/moduleRoutes';
import { PartnersPathList } from 'modules/admin-panel/router/partnersRoutes';
import { filterNavbarRoutes } from 'helpers';
import {
	getFromSessionStorage,
	saveToSessionStorage,
	SessionStorageKeyTypes,
} from 'utils/clientStorage';
import {
	ChevronIcon,
	ClientIcon,
	IIconProps,
	PartnerIcon,
	QueryIcon,
	ResultIcon,
	TemplateIcon,
	SurveyTrackingIcon,
} from 'components/icons';
import { toggleNavbar } from 'modules/admin-panel/main/main.actions';
import { isNavbarOpenedSelector } from 'modules/admin-panel/main/selectors';
import { currentUserIdSelector } from 'modules/admin-panel/selectors/auth';

import './navbar.styles.scss';

interface RenderIconProps extends IIconProps {
	name: string;
}

function RenderIcon({ name, ...props }: RenderIconProps) {
	const icons: Record<string, React.ElementType> = {
		[AppRouteNamesList.partners]: PartnerIcon,
		[AppRouteNamesList.clients]: ClientIcon,
		[AppRouteNamesList.templates]: TemplateIcon,
		[AppRouteNamesList.result]: ResultIcon,
		[AppRouteNamesList.queries]: QueryIcon,
		[AppRouteNamesList.surveyTracking]: SurveyTrackingIcon,
	};

	const IconToRender = icons[name];

	return IconToRender ? <IconToRender {...props} /> : null;
}

interface NavbarProps {
	role: RoleTypes;
}

export const Navbar = ({ role }: NavbarProps) => {
	const { t } = useTranslation('navbar');
	const dispatch = useDispatch();

	const isOpened = useSelector(isNavbarOpenedSelector);
	const partnerId = useSelector(currentUserIdSelector);
	const isSA = role === RoleTypes.SUPER_ADMIN;

	useEffect(() => {
		dispatch(toggleNavbar(getIsNavExpandedFromStorage()));
	}, []);

	function handleToggleNavbar() {
		saveToSessionStorage(SessionStorageKeyTypes.isNavbarExpanded, !isOpened);
		dispatch(toggleNavbar(!isOpened));
	}

	function getIsNavExpandedFromStorage() {
		return getFromSessionStorage(SessionStorageKeyTypes.isNavbarExpanded);
	}

	return (
		<aside className={`navbar ${isOpened ? 'navbar--opened' : ''}`}>
			<ul className="navbar-list">
				<li className="navbar-list__chevrons">
					<button
						type="button"
						className="navbar-list__btn"
						onClick={handleToggleNavbar}
					>
						<ChevronIcon iconClass="navbar-list__chevron" />
						<ChevronIcon iconClass="navbar-list__chevron" />
					</button>
				</li>
				{moduleRoutesList.map((route) => {
					const isPartnerRoute = route.name === AppRouteNamesList.partners;
					const name =
						!isSA && isPartnerRoute ? AppRouteNamesList.partner : route.name;
					const path =
						!isSA && isPartnerRoute
							? {
									pathname: `${route.path}${PartnersPathList.details}`,
									state: {
										id: partnerId,
									},
							  }
							: route.path;

					return filterNavbarRoutes(route, role) ? (
						<li className="navbar-list__item" key={name}>
							<Link className="navbar-list__link" to={path}>
								<RenderIcon
									name={route.name}
									iconClass={`navbar-list__icon ${
										route.name === AppRouteNamesList.result
											? 'navbar-list__icon--result'
											: ''
									}`}
								/>
								<p className="navbar-list__title">{t(name)}</p>
							</Link>
						</li>
					) : null;
				})}
			</ul>
		</aside>
	);
};
